import * as PSLib from "poolside-js-lib";
import externalConfiguration from "../../externalConfiguration";
import Locale from "../../locale";
import { context } from '../../components/AppContext';

function log() {
    if (context.userInfo?.admin) {
        console.debug.apply(console, Object.values(arguments));
    }
}

const errorCodes = {
    missingRegexpInCatalog: 1,
    missingRegexpInAddressingRules: 2,
}


export default function getCompatibleAttendants(devices) {
    log("getCompatibleAttendants() triggered for devices with uuid:", devices?.map(item => item.uuid));

    const compatibleAttendants = [];

    // ATTENDANT TYPE LOOP
    Object.entries(externalConfiguration.platformCapabilities).forEach(([ keyPlatformCapability, itemPlatformCapability ]) => { 
        log("[ConfiguratorLogic] getCompatibleAttendants() 📘 checking compatiblity for:", keyPlatformCapability);

        const config = {
            Controllers: [ {
                UUID: itemPlatformCapability.Controllers[ 0 ].ControllerUUID,
                Number: 1,
            } ],
            Devices: [],
        };

        let idxDevices = 0;
        let notFoundSuitablePort = false;
        if (devices?.length) do {
            // DEVICE LOOP
            const itemDevice = devices[ idxDevices++ ];
            log("[ConfiguratorLogic] getCompatibleAttendants() 🌟 trying to add device:", itemDevice);


            let idxPortNumnbers = 0;
            let deviceConfig;

            const portNumbers = itemPlatformCapability.Controllers[ 0 ].Modules[ 0 ].PortTypes[
                PSLib.Config.translatePortTypeHwConfigToPlatformCapabilities(itemDevice.portType)
            ];
            if (!portNumbers?.length) {
                log("[ConfiguratorLogic] getCompatibleAttendants() ❌ Port type the device uses not available in platform capabilities. Device:", itemDevice);
                notFoundSuitablePort = true;
            } else {
                do {
                    // PORT NUMBER LOOP
                    const configToTest = JSON.parse(JSON.stringify(config));
                    const itemPortNumber = portNumbers[ idxPortNumnbers++ ];
                    deviceConfig = tryDeviceOnPort(itemDevice, itemPortNumber.PortNumber, configToTest, itemPlatformCapability);
                } while(idxPortNumnbers < portNumbers.length && !deviceConfig);

                if (deviceConfig) {
                    config.Devices.push(deviceConfig);
                    log("[ConfiguratorLogic] getCommpatibleAttendants() ℹ️ devices successfully configured so far:", config.Devices.length)
                } else {
                    // Could not found a suitble port for the device
                    // Break device loop, if at least no port for one device, means this atendant type wont work.
                    notFoundSuitablePort = true;
                }
            }
            

        } while ( idxDevices < devices.length && !notFoundSuitablePort);

        if (notFoundSuitablePort) {
            log("[ConfiguratorLogic] getCompatibleAttendants() 👎 Not found suitable port for all devices. Config before adding the last device:", JSON.stringify(config, undefined, 4));
        } else {
            log("[ConfiguratorLogic] getCompatibleAttendants() 👍 Found suitable port for all devices. Config:", JSON.stringify(config, undefined, 4));

            compatibleAttendants.push(keyPlatformCapability);
        }
    });

    log("[ConfiguratorLogic] getCompatibleAttendants() compatibleAttendants:", compatibleAttendants);
    return compatibleAttendants;
}

/**
 * @param {object} itemDevice 
 * @param {number} portNumberStart. 
 * @param {object} configToTest 
 * @param {obejct} PlatformCapabilities 
 * @returns {object|undefined}
 * - object: DeviceConfig with port number and address fields set.
 * - undefined: it was not able to add devuce to the config by `portNumber` and get a valid port usage.
 */
function tryDeviceOnPort(itemDevice, portNumberStart, configToTest, PlatformCapabilities) {
    log("[ConfiguratorLogic] getCompatibleAttendants() 🔌 trying portNumberStart:", portNumberStart);

    const deviceConfig = {
        UUID: itemDevice.uuid,
        DeviceType: itemDevice.deviceType,
        HardwareDevice: {
            PrimaryCatalogId: itemDevice.primaryCatalogId,
            SecondaryCatalogId: itemDevice.secondaryCatalogId,
            LightSettings: {
                CompatibleBrandId: itemDevice.CompatibleBrandId,
                ColorAndShowLibraryId: itemDevice.ColorAndShowLibraryId,
                ColorAndShowSelectionMethodId: itemDevice.ColorAndShowSelectionMethodId,
            },
            HVACSettings: {
                SupportCooling: itemDevice.deviceType === PSLib.Config.DeviceTypes.HEATPUMP,
                SupportHeating: true,
            },
            Controller: configToTest.Controllers[ 0 ].UUID,
            Module: "1",
            Ports: [],
            WaterLevelSensorSettings: {
                WaterLevelSensor: {
                }
            },
            
        }
    };

    const primaryPort = PSLib.Config.getPrimaryPort(deviceConfig.DeviceType);
    if (primaryPort === "HardwareDevice.Ports") {
        // Make sure port type at least for the first port is set so getHardwareDevicePortsMinCount() could determine the selected port type.
        deviceConfig.HardwareDevice.Ports[ 0 ] = {
            Type: itemDevice.portType,
        }
        const minPortCount = PSLib.Config.getHardwareDevicePortsMinCount(deviceConfig);
        const maxPortCount = PSLib.Config.getHardwareDevicePortsMaxCount(deviceConfig);
        const allPorts = PSLib.Config.getAllPortTypes(deviceConfig);
        log("[ConfiguratorLogic] getCompatibleAttendants() port count min:", minPortCount, "max:", maxPortCount, "allPorts:", allPorts);

        const portType = PSLib.Config.translatePortTypeHwConfigToPlatformCapabilities(itemDevice.portType);
        const portTypes = PlatformCapabilities.Controllers
            ?.find(item => item.ControllerUUID === deviceConfig.HardwareDevice.Controller)
            ?.Modules?.find(item => item.ModuleNumber === deviceConfig.HardwareDevice.Module)
            ?.PortTypes[ portType ]
        ;
        for (let i = 0; i < maxPortCount; i++) {
            const portNumber = portNumberStart + i;
            if (portTypes?.find(item => item.PortNumber === portNumber)) {
                if (!deviceConfig.HardwareDevice.Ports[ i ]) {
                    deviceConfig.HardwareDevice.Ports[ i ] = {
                        Type: itemDevice.portType,
                    }
                }
                deviceConfig.HardwareDevice.Ports[ i ].Number = portNumber;
            } else {
                log("[ConfiguratorLogic] tryDeviceOnPort() ❌ Ran out of available ports. Port number:", portNumber, "is not available for:", portType);
                return;
            }
        }
    } else {
        PSLib.Utils.Object.subSet(deviceConfig, primaryPort + ".Type", itemDevice.portType);
        PSLib.Utils.Object.subSet(deviceConfig, primaryPort + ".Number", portNumberStart);
    }


    configToTest.Devices.push(deviceConfig);


    let addressingRuleStartIdx = 0;
    let retry = true;
    let maxTryCount = 1000;
    do {  
        maxTryCount--;
        const retSetAddresses = setAddresses(deviceConfig, addressingRuleStartIdx++);
        if (retSetAddresses) {
            log("[ConfiguratorLogic] tryDeviceOnPort() ❌ Ran out of available address space. Won't try more addressing on this port.");
            break;
        }
        
        // log("getCompatibleAttendants() configToTest:", JSON.parse(JSON.stringify(configToTest)))

        const portUsage = PSLib.Config.getPortUsage({
            configs: [ {
                fileName: PSLib.Config.ConfigRecordFileNames.HardwareConfig,
                data: configToTest,
            } ],
            siteStates: {
                PlatformCapabilities: JSON.stringify(
                    PlatformCapabilities
                ),
                CohabitableBuses: JSON.stringify(
                    externalConfiguration.cohabitableBuses
                ),
            }
        });
        
        if (portUsage.Invalid) {
            log("[ConfiguratorLogic] tryDeviceOnPort() ❌ invalid port usage");
            // log("[ConfiguratorLogic] tryDeviceOnPort() ❌ invalid port usage:", JSON.stringify(portUsage, undefined, 4));

            let type = deviceConfig.HardwareDevice.Ports[ 0 ]?.Type;
            let number = deviceConfig.HardwareDevice.Ports[ 0 ]?.Number;
            if(primaryPort !== "HardwareDevice.Ports") {
                // Other port props are not arrays
                type = itemDevice.portType;
                number = portNumberStart;
            }

            const port = portUsage.Controllers
                ?.find(item => item.ControllerUUID === deviceConfig.HardwareDevice.Controller)
                ?.Modules?.find(item => item.ModuleNumber === deviceConfig.HardwareDevice.Module)
                ?.PortTypes[
                    PSLib.Config.translatePortTypeHwConfigToPlatformCapabilities(type)
                ]
                ?.find(item => item.PortNumber === number)
            ;
            if (!port) {
                // console.error("[ConfiguratorLogic] tryDeviceOnPort()  ⚠️ could not find entry/entries in port usage for the device. Could not detect error reason. Device:", deviceConfig, "portUsage", JSON.stringify(portUsage, undefined, 4));
                if (retSetAddresses === false) {
                    console.error("[ConfiguratorLogic] tryDeviceOnPort() ⚠️ could not find entry/entries in port usage for the device. Could not detect error reason. Won't try more addressing on this port, this device does not use addresses.");
                    retry = false;
                } else {
                    console.error("[ConfiguratorLogic] tryDeviceOnPort() ⚠️ could not find entry/entries in port usage for the device. Could not detect error reason.");
                }
            } else {
                // log("[ConfiguratorLogic] tryDeviceOnPort() Port:", JSON.stringify(port, undefined, 4));

                if (port?.InvalidReason) {
                    log("[ConfiguratorLogic] tryDeviceOnPort() Port level issue. Won't try more addressing on this port. InvalidReason:", port?.InvalidReason);
                    // Only try again if the InvalidReason is becasue of address uniqueness
                    retry = false;
                } else {
                    // Get item of BusClasses that has the device configured
                    const busClass = port.BusClasses?.find(itemBusClasses =>
                        itemBusClasses.ConfiguredDevices?.some(item => item.UUID === deviceConfig.UUID)
                    );
                    // log("[ConfiguratorLogic] tryDeviceOnPort() busclass:", JSON.stringify(busClass, undefined, 4));
                    /** @todo add code instead of matching string */
                    if (busClass?.InvalidReason?.indexOf("not unique") < 0) {
                        log("[ConfiguratorLogic] tryDeviceOnPort() Bus class level issue but not about addressing. Won't try more addressing on this port. InvalidReason:", busClass?.InvalidReason);
                        retry = false;
                    } else if (retSetAddresses === false) {
                        log("[ConfiguratorLogic] tryDeviceOnPort() Bus class level issue with addressing. Won't try more addressing on this port, this device does not use addresses. InvalidReason:", busClass?.InvalidReason);
                        retry = false;
                    } else {
                        log("[ConfiguratorLogic] tryDeviceOnPort() Bus class level issue with addressing. Try next address on this port. InvalidReason:", busClass?.InvalidReason);
                    }
                }
            }
        } else {
            log("[ConfiguratorLogic] tryDeviceOnPort() ✅ valid port usage");
            // log("[ConfiguratorLogic] tryDeviceOnPort() valid port usage:", JSON.stringify(portUsage, undefined, 4));
            return deviceConfig;
        }
    } while (retry && maxTryCount > 0);

    if (maxTryCount === 0) console.error("[ConfiguratorLogic] tryDeviceOnPort() ❌ Safety counter reached. Giving up trying addresses on this port.");
}

/**
 * Try to add fields to deviceConfig.HardwareDevice.
 * @param {object} deviceConfig 
 * @param {number} addressingRuleStartIdx 
 * @returns undefined|boolean (Falsy return value means all ok)
 * undefined: all fields has set
 * false: no need to set addresses
 * true: ran out of the address space
 * @throws at unexpected cases e.g. missing addressing rule or missing regex in the catalog
 */
function setAddresses(deviceConfig, addressingRuleStartIdx) {
    log("[ConfiguratorLogic] setAddresses() triggered for device:", deviceConfig.UUID, "addressingRuleStartIdx:",addressingRuleStartIdx);
    const catalogEntry = PSLib.Catalog.getCatalogEntryByDeviceConfig(deviceConfig);
    if (!catalogEntry) return false;

    // No need to set addressees.
    if (!catalogEntry.configurableFields?.length) return false;

    let idxConfigurableFields = 0;
    let addressingRuleIdx = addressingRuleStartIdx;
    while (idxConfigurableFields < catalogEntry.configurableFields.length) {
        const itemConfigurableFields = catalogEntry.configurableFields[ idxConfigurableFields ];
        log("[ConfiguratorLogic] setAddresses() itemConfigurableFields, idx:", idxConfigurableFields, itemConfigurableFields?.Id);

        // Won't be able to determine how to generate address at all
        if (!itemConfigurableFields.Regex) {
            const err = new Error("[ConfiguratorLogic] setAddresses() No regex in the catalog for configurable field: " + JSON.stringify(itemConfigurableFields));
            err.msgForUser = Locale.gettext("There was an error with the selection.  (Code = %d)  Please reach out to our sales team at sales@poolside.tech or +1 (267) 810-0600", errorCodes.missingRegexpInCatalog);
            throw err;
        }
        
        const addressingRule = externalConfiguration.addressingRules[ itemConfigurableFields.Regex ];
        // Won't be able to generate address, no predefined generator for this pattern
        if (!addressingRule) {
             const err = new Error("[ConfiguratorLogic] setAddresses() Missing addressing rule for regexp: " + itemConfigurableFields.Regex);
            err.msgForUser = Locale.gettext("There was an error with the selection.  (Code = %d)  Please reach out to our sales team at sales@poolside.tech or +1 (267) 810-0600", errorCodes.missingRegexpInAddressingRules);
            throw err;
        }

        if (addressingRuleIdx <= addressingRule.maxIndex) {
            PSLib.Utils.Object.subSet(deviceConfig.HardwareDevice, itemConfigurableFields.Id, addressingRule.getAddress(addressingRuleIdx++));
        } else {
            // ran out of the address space before setting address for all the fields
            return true;
        }
        idxConfigurableFields++;
    }

    // Reching this point means we were able to set an address for all configurable fields
    return;
}
